// Single source of truth for every module the sidebar and router care
// about. Each entry pairs a kebab-case `key` (matches MODULE_NAME) with
// the metadata both consumers need — translate it once via `labelKey`,
// render the icon, point the router at `path`, and express hierarchy
// through `parent`.
//
// `permissionKeys` controls visibility in the sidebar:
//
//   - `permissionKeys: ['a', 'b']`  — visible when ANY of the listed
//     slugs is granted. Used to fold several backend modules into one
//     top-level nav item, e.g. Facilities is shown for either
//     `facility` or `coldroom` permission.
//   - `permissionKeys: ['a']`  — gated by a single slug (the usual
//     case for leaf items like Alarms or Documents).
//   - `permissionKeys: []`  — "common" item, always visible regardless
//     of the user's role. Use sparingly; intended for app-shell
//     entries like Activity and Global Settings that every signed-in
//     user needs.
//   - Omitted entirely  — treated identically to `[]` (common).
//
// Adding a new module is one row in this object; the sidebar
// (`useNavigationConfig`) and the route table (`allRoutes.jsx`) both
// read from it so the UI and routing can't drift apart.

import {
  LayoutGrid,
  House,
  Cpu,
  Bell,
  BellDot,
  BellRing,
  BarChart3,
  FileText,
  Settings,
  ShieldCheck,
  Users,
  CircleHelp,
  Building2,
  Activity,
  History,
  UserCog,
  Sparkles,
} from 'lucide-react'

import { MODULE_NAME, ROUTES } from '../constants'

/**
 * Which backend module's permission governs each app module.
 *
 * The backend's permission payload (`GET /guardian-users/permission`) keys
 * modules by a **backend slug** (`moduleSlug`) plus a numeric `moduleId`.
 * The app's own `MODULE_NAME` ids are mostly identical, but two differ:
 *
 *   `role`  → `roles-and-permission`
 *   `branch` → `branch-management`
 *
 * `PERMISSION_MODULE` is the bridge. `src/utils/branchPermissions.js`
 * resolves an app module key through this map before looking the module up
 * in the cached permission payload.
 *
 * `moduleId` is the fallback matcher for backend rows that ship a null
 * `moduleSlug` — some rows only identify themselves by id.
 *
 * Note: V-Tag, Access Point, External Module and Input Module are
 * sub-tables of Device Management and all run on the **`device`**
 * permission, not on their own backend module rows.
 */
export const PERMISSION_MODULE = {
  [MODULE_NAME.DASHBOARD]: { slug: 'dashboard', moduleId: 1 },
  [MODULE_NAME.FACILITY]: { slug: 'facility', moduleId: 2 },
  [MODULE_NAME.COLDROOM]: { slug: 'area', moduleId: 3 },
  [MODULE_NAME.DEVICE]: { slug: 'device-management', moduleId: 4 },
  [MODULE_NAME.V_TAG]: { slug: 'device-management', moduleId: 4 },
  [MODULE_NAME.ACCESS_POINT]: { slug: 'device-management', moduleId: 4 },
  [MODULE_NAME.EXTERNAL_MODULE]: { slug: 'device-management', moduleId: 4 },
  [MODULE_NAME.INPUT_MODULE]: { slug: 'device-management', moduleId: 4 },
  [MODULE_NAME.ALARMS]: { slug: 'alarms', moduleId: 5 },
  [MODULE_NAME.NOTIFICATION]: { slug: 'notifications', moduleId: 6 },
  [MODULE_NAME.REMINDER]: { slug: 'reminder', moduleId: 7 },
  [MODULE_NAME.REPORTS]: { slug: 'reports', moduleId: 8 },
  [MODULE_NAME.DOCUMENTS]: { slug: 'documents', moduleId: 9 },
  [MODULE_NAME.WHATS_NEW]: { slug: "what's-new", moduleId: 10 },
  [MODULE_NAME.KPI]: { slug: 'data-analysis', moduleId: 11 },
  [MODULE_NAME.USER_MANAGEMENT]: { slug: 'user-management', moduleId: 12 },
  [MODULE_NAME.ROLE]: { slug: 'roles-and-permission', moduleId: 13 },
  [MODULE_NAME.BRANCH]: { slug: 'branch-management', moduleId: 14 },
  [MODULE_NAME.HELP]: { slug: 'help', moduleId: 15 },
  [MODULE_NAME.SUPPORT_TICKET]: { slug: 'support-ticket', moduleId: 16 },
  [MODULE_NAME.GLOBAL_SETTINGS]: { slug: 'global-settings', moduleId: 17 },
  [MODULE_NAME.AUDIT_TRSILS]: { slug: 'audit-trails', moduleId: 18 },
}

// Convenience accessor used by the registry below so every entry can carry
// its backend slug without repeating the literal.
const backendSlugOf = (key) => PERMISSION_MODULE[key]?.slug ?? key

/**
 * Module registry.
 *
 *   key            — matches `MODULE_NAME` (kebab-case id from the platform).
 *   labelKey       — i18n string id; the active locale's `t()` resolves it.
 *   icon           — Lucide icon component.
 *   path           — URL the router listens on. `null` for grouping-only
 *                    modules (parents without their own page) and for
 *                    placeholders that don't have a real route yet.
 *   parent         — another module's key, or `null` for top-level. Items
 *                    whose `parent` is set are rendered as `subItems` under
 *                    the matching entry; the parent shows only when at
 *                    least one of its children is visible.
 *   order          — integer controlling sidebar ordering (ascending).
 *   backendSlug    — the slug the backend permission payload uses for this
 *                    module (see `PERMISSION_MODULE`).
 *   permissionKeys — see module-level comment above. Drives the
 *                    sidebar's permission filter via OR-semantics; route
 *                    guards continue to key off `key` (single slug).
 */
export const modules = {
  [MODULE_NAME.DASHBOARD]: {
    key: MODULE_NAME.DASHBOARD,
    labelKey: 'Dashboard',
    icon: LayoutGrid,
    path: ROUTES.DASHBOARD,
    parent: null,
    order: 10,
    backendSlug: backendSlugOf(MODULE_NAME.DASHBOARD),
    permissionKeys: [MODULE_NAME.DASHBOARD],
  },
  [MODULE_NAME.FACILITY]: {
    key: MODULE_NAME.FACILITY,
    labelKey: 'Facilities',
    icon: House,
    path: ROUTES.FACILITIES,
    parent: null,
    order: 20,
    backendSlug: backendSlugOf(MODULE_NAME.FACILITY),
    // Both `facility` and `coldroom` permissions surface the same
    // nav item — they're sibling pages under the Facilities tree.
    permissionKeys: [MODULE_NAME.FACILITY, MODULE_NAME.COLDROOM],
  },
  [MODULE_NAME.SENSOR]: {
    key: MODULE_NAME.SENSOR,
    labelKey: 'Device Management',
    icon: Cpu,
    path: ROUTES.SENSORS,
    parent: null,
    order: 30,
    backendSlug: backendSlugOf(MODULE_NAME.SENSOR),
    // Device Management covers the catch-all `sensor` module plus the
    // physical device sub-types — Access Points, V-Tags, External Modules
    // and Input Modules all run on the `device` permission, so `sensor`
    // and `device` are the two slugs that can reveal this nav item.
    permissionKeys: [MODULE_NAME.SENSOR, MODULE_NAME.DEVICE],
  },
  [MODULE_NAME.ALARMS]: {
    key: MODULE_NAME.ALARMS,
    labelKey: 'Alarms',
    icon: Bell,
    path: ROUTES.ALARMS,
    parent: null,
    order: 40,
    backendSlug: backendSlugOf(MODULE_NAME.ALARMS),
    permissionKeys: [MODULE_NAME.ALARMS],
  },
  [MODULE_NAME.NOTIFICATION]: {
    key: MODULE_NAME.NOTIFICATION,
    labelKey: 'Notifications',
    icon: BellDot,
    path: ROUTES.NOTIFICATIONS,
    parent: null,
    // Top-level module — the full-page notification inbox. Sits
    // between Alarms (40) and Reports (50), using `BellDot` (same Bell
    // family as Alarms) so the two read as siblings in the sidebar while
    // the dot indicator flags this one as the unread / notification-aware
    // module. Was previously a sub-tab inside Alarms, then a tab inside
    // the combined "Notifications and Reminder" page.
    order: 41,
    backendSlug: backendSlugOf(MODULE_NAME.NOTIFICATION),
    // The backend ships Notifications (id 6) and Reminder (id 7) as two
    // separate modules, but this nav item covers both pages — visible
    // when EITHER one grants `view`.
    permissionKeys: [MODULE_NAME.NOTIFICATION, MODULE_NAME.REMINDER],
  },
  [MODULE_NAME.REMINDER]: {
    key: MODULE_NAME.REMINDER,
    labelKey: 'Reminders',
    icon: BellRing,
    path: ROUTES.REMINDERS,
    parent: null,
    // Top-level module — the branch-wide reminders list, right below
    // Notifications. Accepts either slug so the page stays reachable for
    // everyone who had it under the old combined "Notifications and
    // Reminder" page (gated by `notification`) while also honouring a
    // dedicated `reminder` grant.
    order: 42,
    permissionKeys: [MODULE_NAME.REMINDER, MODULE_NAME.NOTIFICATION],
  },
  [MODULE_NAME.REPORTS]: {
    key: MODULE_NAME.REPORTS,
    labelKey: 'Reports',
    icon: BarChart3,
    path: ROUTES.REPORTS,
    parent: null,
    order: 50,
    backendSlug: backendSlugOf(MODULE_NAME.REPORTS),
    permissionKeys: [MODULE_NAME.REPORTS],
  },
  [MODULE_NAME.DOCUMENTS]: {
    key: MODULE_NAME.DOCUMENTS,
    labelKey: 'Documents',
    icon: FileText,
    path: ROUTES.DOCUMENTS,
    parent: null,
    order: 60,
    backendSlug: backendSlugOf(MODULE_NAME.DOCUMENTS),
    permissionKeys: [MODULE_NAME.DOCUMENTS],
  },
  [MODULE_NAME.KPI]: {
    key: MODULE_NAME.KPI,
    // Sidebar label intentionally reads "KPI" (per product request) even
    // though the breadcrumb inside the page uses "Monitoring / Data
    // analysis" — the breadcrumb is the page context, the sidebar is the
    // module shortcut.
    labelKey: 'Data analysis',
    icon: Activity,
    path: ROUTES.KPI,
    parent: null,
    order: 70,
    backendSlug: backendSlugOf(MODULE_NAME.KPI),
    permissionKeys: [MODULE_NAME.KPI],
  },
  [MODULE_NAME.BRANCH_AND_USER]: {
    key: MODULE_NAME.BRANCH_AND_USER,
    labelKey: 'Branch & User',
    icon: UserCog,
    path: null,
    parent: null,
    order: 80,
    // Parent group — visibility is driven entirely by whether any
    // child is visible; no permissionKeys field of its own.
  },
  [MODULE_NAME.ROLE]: {
    key: MODULE_NAME.ROLE,
    labelKey: 'Roles & Permission',
    icon: ShieldCheck,
    path: ROUTES.ROLES,
    parent: MODULE_NAME.BRANCH_AND_USER,
    order: 81,
    backendSlug: backendSlugOf(MODULE_NAME.ROLE),
    permissionKeys: [MODULE_NAME.ROLE],
  },
  [MODULE_NAME.USER_MANAGEMENT]: {
    key: MODULE_NAME.USER_MANAGEMENT,
    labelKey: 'User Management',
    icon: Users,
    path: ROUTES.USER_MANAGEMENT,
    parent: MODULE_NAME.BRANCH_AND_USER,
    order: 82,
    backendSlug: backendSlugOf(MODULE_NAME.USER_MANAGEMENT),
    // The backend surfaces User Management under either the
    // `user-management` slug (its current form) or the legacy `user` slug
    // depending on how the role row was authored. They're the same logical
    // permission — accept either so a missing one doesn't accidentally hide
    // the page.
    permissionKeys: [MODULE_NAME.USER_MANAGEMENT, MODULE_NAME.USER],
  },
  [MODULE_NAME.BRANCH]: {
    key: MODULE_NAME.BRANCH,
    labelKey: 'Branch Management',
    icon: Building2,
    path: ROUTES.BRANCH_MANAGEMENT,
    parent: MODULE_NAME.BRANCH_AND_USER,
    order: 83,
    backendSlug: backendSlugOf(MODULE_NAME.BRANCH),
    permissionKeys: [MODULE_NAME.BRANCH],
    // Visibility is driven purely by the branch-scoped permission payload
    // (`localStorage['branchPermissions']`). A USER-type account whose
    // role carries a `branch-management` / `view` row must see the entry —
    // the role row IS the grant, so no account-type gate is applied here.
    // The branch *switcher* (per-user branch context) is a separate
    // component and stays available to every signed-in user.
  },
  [MODULE_NAME.HELP]: {
    key: MODULE_NAME.HELP,
    labelKey: 'Help',
    icon: CircleHelp,
    path: ROUTES.HELP,
    parent: null,
    order: 90,
    backendSlug: backendSlugOf(MODULE_NAME.HELP),
    permissionKeys: [MODULE_NAME.HELP],
  },
  [MODULE_NAME.WHATS_NEW]: {
    key: MODULE_NAME.WHATS_NEW,
    labelKey: "What's New",
    icon: Sparkles,
    path: ROUTES.WHATS_NEW,
    parent: null,
    // Sits between Documents (60) and KPI (70).
    order: 65,
    backendSlug: backendSlugOf(MODULE_NAME.WHATS_NEW),
    // Permission-gated — the backend ships "What's New" as module 10
    // (slug `what's-new`), so the item only shows when the branch
    // payload grants it an explicit `view` action.
    permissionKeys: [MODULE_NAME.WHATS_NEW],
  },
  [MODULE_NAME.AUDIT_TRSILS]: {
    key: MODULE_NAME.AUDIT_TRSILS,
    labelKey: 'Audit Trails',
    icon: History,
    path: ROUTES.ACTIVITY,
    parent: null,
    order: 100,
    backendSlug: backendSlugOf(MODULE_NAME.AUDIT_TRSILS),
    // Permission-gated — the backend ships Audit Trails rows with a null
    // `moduleSlug` but `moduleId: 18`, so the id fallback in
    // `PERMISSION_MODULE` is what actually grants access here.
    permissionKeys: [MODULE_NAME.AUDIT_TRSILS],
  },
  [MODULE_NAME.GLOBAL_SETTINGS]: {
    key: MODULE_NAME.GLOBAL_SETTINGS,
    labelKey: 'Global Settings',
    icon: Settings,
    path: ROUTES.GLOBAL_SETTINGS,
    parent: null,
    order: 110,
    backendSlug: backendSlugOf(MODULE_NAME.GLOBAL_SETTINGS),
    permissionKeys: [MODULE_NAME.GLOBAL_SETTINGS],
  },
}

/** Flat list of registry entries in stable (insertion) order. */
export const moduleList = Object.values(modules)

/**
 * Fast path → module-key lookup. `allRoutes.jsx` uses this so each
 * route row's `requiredModule` is derived from its `path` — the router
 * and the sidebar can't disagree on which module owns which URL.
 * Entries with `path: null` are skipped because they don't back a
 * route.
 */
export const moduleKeyByPath = Object.fromEntries(
  Object.values(modules)
    .filter((m) => m.path)
    .map((m) => [m.path, m.key])
)
